/**
 * buildScopedCss — rewrites admin-authored custom CSS so every selector is
 * prefixed with the given scope id, preventing it from leaking outside its
 * public view's content container (navbar, breadcrumbs, other tables' pages).
 *
 * Implementation compiles the raw CSS on its own first (a lone "pre-pass")
 * so stylis normalizes it — critically, this discards/repairs any unbalanced
 * brace before it ever gets a chance to escape the scope wrapper. Only the
 * normalized, well-formed output is then wrapped in `#{scopeId} { ... }` and
 * compiled a second time to apply the actual scoping. A single-pass
 * wrap-then-compile is NOT safe: a stray `}` in the admin's raw CSS would
 * terminate the wrapper early and let everything after it compile as
 * unscoped, top-level CSS.
 *
 * `@import` and `@font-face` at-rules are removed. Both trigger network
 * requests regardless of what selector wraps them, so scoping cannot
 * meaningfully contain them.
 *
 * Removal is a STRUCTURAL filter over stylis's compiled AST, not a textual
 * substitution on the source. Filtering raw text with a regex is not
 * defensible here: `.replace()` runs a single pass, so surviving fragments
 * can re-concatenate into the very rule that was stripped
 * (`@imp@import;ort url(...)`); a regex must hard-code a case and would miss
 * `@Font-Face` (at-rule names are case-insensitive per spec); and an empty
 * comment between the name and its block defeats naive whitespace matching
 * (`\s*` does not match a comment). Once `compile()` has run, none of those
 * apply — the
 * parser has already discarded comments, tokenized the at-rule name into
 * `element.type`, and produced a tree of typed nodes with no raw text left to
 * reassemble.
 */

import { compile, serialize, stringify, middleware } from 'stylis';

// At-rule names, normalized (lowercased, CSS escapes resolved, no leading `@`),
// that must never survive into the rendered <style> tag.
const BLOCKED_AT_RULES = new Set(['import', 'font-face']);

const AT_SIGN = 64; // '@'

// Characters that may appear unescaped in a CSS identifier. Anything else
// (whitespace, `{`, `(`, `;`, `"`, a second `@`, ...) terminates the name.
const IDENT_CHAR_RE = /[A-Za-z0-9_\-\u0080-\uFFFF]/;

// A CSS unicode escape: `\` + 1-6 hex digits + at most one trailing whitespace
// (the whitespace is a delimiter and is consumed as part of the escape).
const UNICODE_ESCAPE_RE = /^\\([0-9A-Fa-f]{1,6})(?:\r\n|[ \n\r\t\f])?/;

/**
 * Reads the at-keyword out of an at-rule's source text and normalizes it the
 * way a browser's tokenizer would: CSS escapes resolved, case folded.
 *
 * This matters because stylis preserves the author's spelling verbatim in
 * `element.type` / `element.value` — it does not case-fold and does not
 * unescape. A browser treats `@font-face`, `@Font-Face`, `@font\-face` and
 * `@\66 ont-face` as the same at-rule, so the filter has to as well.
 *
 * @param {string} text source starting at the `@`
 * @return {string} normalized name without the leading `@` (may be empty)
 */
function normalizeAtKeyword(text) {
  let name = '';
  let i = 1; // skip the leading '@'

  while (i < text.length) {
    const ch = text[i];

    if (ch === '\\') {
      const unicode = UNICODE_ESCAPE_RE.exec(text.slice(i));
      if (unicode) {
        const codePoint = parseInt(unicode[1], 16);
        // 0 and out-of-range code points become U+FFFD per CSS Syntax.
        name += String.fromCodePoint(codePoint > 0 && codePoint <= 0x10ffff ? codePoint : 0xfffd);
        i += unicode[0].length;
        continue;
      }
      if (i + 1 < text.length) {
        // Any other escaped character stands for itself.
        name += text[i + 1];
        i += 2;
        continue;
      }
      break;
    }

    if (!IDENT_CHAR_RE.test(ch)) break;

    name += ch;
    i += 1;
  }

  return name.toLowerCase();
}

/**
 * @param {object} element a stylis AST element
 * @return {string[]} every normalized at-keyword this element could be read as,
 *   or an empty array if it is not an at-rule at all
 */
function atKeywordsOf(element) {
  const names = [];
  const { type, value } = element;

  if (typeof type === 'string' && type.charCodeAt(0) === AT_SIGN) {
    names.push(normalizeAtKeyword(type));
  }
  // `value` holds the full prelude (`@font\066 ace`, `@media (max-width:600px)`).
  // stylis splits an escape that ends in whitespace across `type`/`props`, so
  // the name can only be reconstructed faithfully from `value`.
  if (typeof value === 'string' && value.charCodeAt(0) === AT_SIGN) {
    names.push(normalizeAtKeyword(value));
  }

  return names;
}

/**
 * Recursively drops `@import` / `@font-face` elements from a compiled stylis
 * AST, at any nesting depth — an attacker can bury a `@font-face` inside
 * `@media` / `@supports` / `@layer`, where it still loads a remote font.
 *
 * Mutates `children` arrays in place (the AST is freshly compiled and owned by
 * this module) and returns the filtered sibling list.
 *
 * @param {object[]} elements
 * @return {object[]}
 */
function filterDangerousAtRules(elements) {
  const kept = [];

  for (const element of elements) {
    if (!element || typeof element !== 'object') continue;

    const names = atKeywordsOf(element);
    if (names.some((name) => BLOCKED_AT_RULES.has(name))) continue;

    // `children` is an array for rules/at-rule blocks and a string for
    // declarations, so only recurse when it is actually a node list.
    if (Array.isArray(element.children)) {
      const hadChildren = element.children.length > 0;
      element.children = filterDangerousAtRules(element.children);

      // An at-rule block whose entire body was filtered away is dropped too.
      // Left in place, stylis serializes some of them (`@layer`) as a bare
      // prelude with no braces, which would run into the next rule.
      if (names.length > 0 && hadChildren && element.children.length === 0) continue;
    }

    kept.push(element);
  }

  return kept;
}

/**
 * @param {string} cssText
 * @return {string} normalized CSS with dangerous at-rules removed
 */
function compileFiltered(cssText) {
  return serialize(filterDangerousAtRules(compile(cssText)), middleware([stringify]));
}

export function buildScopedCss(cssText, scopeId) {
  if (!cssText) return '';
  // Pass 1: normalize (repairs unbalanced braces) and strip dangerous at-rules.
  const normalized = compileFiltered(cssText);
  if (!normalized) return '';
  // Pass 2: apply the scope prefix. Filtered again purely as defense in depth —
  // pass 1's output is already clean, so this is a no-op on any honest input.
  return compileFiltered(`#${scopeId}{${normalized}}`);
}
